import { traceNetwork } from "../model/inp/networkTrace.ts";
import type { ElementRef } from "../model/inp/properties.ts";
import type { SwmmProject } from "../model/inp/types.ts";
import type { useLongitudinalProfile } from "./useLongitudinalProfile.ts";
import type { useNetworkTrace } from "./useNetworkTrace.ts";

/**
 * The two "pick a node on the map" flows (LongitudinalProfileDialog's start/
 * end, ToolsMenu's trace upstream/downstream) both intercept a click
 * regardless of whatever edit `mode` is active — a click while either is
 * armed is a pick attempt, never a normal selection/drawing click.
 * LongitudinalProfileDialog's own picking wins when somehow both are armed
 * (mutually exclusive in practice — App.tsx's own toolbar closes one before
 * arming the other). A non-node click while picking is simply ignored (both
 * flows only ever run node-to-node) — still consumed, so it doesn't fall
 * through to a normal click either.
 */
export function resolveElementPick(
  ref: ElementRef,
  project: SwmmProject | null,
  longitudinalProfile: ReturnType<typeof useLongitudinalProfile>,
  networkTrace: ReturnType<typeof useNetworkTrace>,
  setSelected: (ref: ElementRef | null) => void
): boolean {
  if (longitudinalProfile.picking) {
    if (ref.category === "node") {
      longitudinalProfile.resolvePick(ref.name);
      setSelected(ref);
    }
    return true;
  }
  // Same shape as longitudinalProfile's pick above, except the trace itself
  // is computed here rather than inside useNetworkTrace.ts, since it needs
  // `project`.
  if (networkTrace.picking && project) {
    if (ref.category === "node") {
      networkTrace.resolvePick(ref.name, traceNetwork(project, ref.name, networkTrace.picking));
      setSelected(ref);
    }
    return true;
  }
  return false;
}

/** Same "pick" precedence as `resolveElementPick` above, for a click on empty map background — reads as "never mind", cancelling the pick rather than completing it. */
export function resolveBackgroundPick(
  longitudinalProfile: ReturnType<typeof useLongitudinalProfile>,
  networkTrace: ReturnType<typeof useNetworkTrace>
): boolean {
  if (longitudinalProfile.picking) {
    longitudinalProfile.cancelPick();
    return true;
  }
  if (networkTrace.picking) {
    networkTrace.cancelPick();
    return true;
  }
  return false;
}
